import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Divider,
  Grid,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import COLORS from 'theme/colors';

type HealthRow = {
  url: string;
  status: 'UP' | 'DOWN' | 'LOADING';
  name?: string;
  version?: string;
  environment?: string;
  date?: string;
  error?: string;
};

const BASE_URL = 'https://' + window.location.hostname + '/api';
const REFRESH_INTERVAL = 60; // seconds

export default function ApplicationHealthDashboard() {
  const [rows, setRows] = useState<HealthRow[]>([]);
  const [lastChecked, setLastChecked] = useState('');
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [loading, setLoading] = useState(false);

  const HEALTH_ENDPOINTS = [
    `${BASE_URL}/patient-mgmt/check-health`,
    `${BASE_URL}/payment-service/check-health`,
    `${BASE_URL}/ehr-sync-service/check-health`,
    `${BASE_URL}/user-mgmt/check-health`,
    `${BASE_URL}/documentation-mgmt/check-health`,
    `${BASE_URL}/secure-messaging-service/check-health`,
    `${BASE_URL}/ehr-service/check-health`,
    `${BASE_URL}/assessment-service/check-health`,
  ];

  const fetchHealth = async () => {
    setLoading(true);
    setCountdown(REFRESH_INTERVAL);
    setLastChecked(new Date().toLocaleString());

    setRows(
      HEALTH_ENDPOINTS.map((url) => ({
        url,
        status: 'LOADING',
      }))
    );

    const results = await Promise.all(
      HEALTH_ENDPOINTS.map(async (url) => {
        try {
          const res = await fetch(url);
          if (!res.ok) throw new Error('Service Down');
          const data = await res.json();

          return {
            url,
            status: 'UP',
            name: data.Name,
            version: data.Version,
            environment: data.Environment,
            date: data.Date,
          } as HealthRow;
        } catch (err: any) {
          return {
            url,
            status: 'DOWN',
            error: err.message,
          } as HealthRow;
        }
      })
    );

    setRows(results);
    setLoading(false);
  };

  // Initial load + auto refresh
  useEffect(() => {
    fetchHealth();

    const refreshInterval = setInterval(() => {
      fetchHealth();
    }, REFRESH_INTERVAL * 1000);

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : REFRESH_INTERVAL));
    }, 1000);

    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, []);

  const statusColor = (status: string) =>
    status === 'UP' ? 'success' : status === 'DOWN' ? 'error' : 'warning';

  return (
    <Container maxWidth='lg'>
      {/* ----------- HEADER ----------- */}
      <Grid item xs={8} sx={{ backgroundColor: COLORS.White, marginTop: 10 }}>
        <Paper
          elevation={10}
          sx={{
            flex: 1,
            p: 2,
            borderRadius: 3,
          }}
        >
          <Stack spacing={2}>
            {/* Header */}
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <Box>
                <Typography variant='h5' fontWeight={600}>
                  Application Health Dashboard
                </Typography>
              </Box>
              <Button
                variant='text'
                startIcon={<RefreshIcon />}
                onClick={fetchHealth}
                disabled={loading}
              />
            </Stack>

            <Divider />
            {/* Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <b>Name</b>
                    </TableCell>
                    <TableCell>
                      <b>Status</b>
                    </TableCell>
                    <TableCell>
                      <b>Last Deployed</b>
                    </TableCell>
                    <TableCell>
                      <b>Description</b>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.url} hover>
                      <TableCell>{row?.name?.toUpperCase() || '-'}</TableCell>
                      <TableCell>
                        <Chip label={row.status} color={statusColor(row.status)} size='small' />
                      </TableCell>
                      <TableCell>{row.date || row.error || '-'}</TableCell>
                      <TableCell>
                        {row?.name?.toUpperCase() === 'PATIENT-MGMT'
                          ? 'This service is responsible for managing patient-related information.'
                          : null}
                        {row?.name?.toUpperCase() === 'PAYMENT-SERVICE'
                          ? 'This is responsible for making payment for each appointment and manges payment releated services.'
                          : null}
                        {row?.name?.toUpperCase() === 'EHR-SYNC-SERVICE' ? 'test' : null}
                        {row?.name?.toUpperCase() === 'USER-MGMT' ? 'test' : null}
                        {row?.name?.toUpperCase() === 'DOCUMENTATION-MGMT' ? 'test' : null}
                        {row?.name?.toUpperCase() === 'SECURE-MESSAGING-SERVICE' ? 'test' : null}
                        {row?.name?.toUpperCase() === 'EHR-SERVICE' ? 'test' : null}
                        {row?.name?.toUpperCase() === 'ASSESSMENT-SERVICE' ? 'test' : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Paper>
      </Grid>
    </Container>
  );
}
